import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Button, Col, Input, Row, Badge,  DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { MoreVertical, PlusCircle } from 'react-feather';
import { FaPen, FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { baseURL } from "../../../../Services/api/baseURL";
import { useNavigate } from "react-router-dom";
import useRbac from "../../../../Hooks/useRbac";

const apiBase = `${baseURL}/api`;

const authHeader = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  return { Authorization: token };
};

const RolesTable = () => {
  const rbac = useRbac();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await axios.get(`${apiBase}/roles`, { headers: authHeader(), params });
      setRoles(res.data?.data || []);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to load roles", text: err?.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (row) => {
    if (row.is_system) {
      Swal.fire({ icon: "error", title: "Cannot delete", text: "System roles cannot be deleted." });
      return;
    }
    const ok = await Swal.fire({
      icon: "warning",
      title: `Delete role "${row.role_name}"?`,
      text: "Employees holding this role will need to be reassigned first.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${apiBase}/roles/${row._id}`, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
      load();
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.employee_count) {
        Swal.fire({
          icon: "warning",
          title: "Cannot delete",
          text: `${data.employee_count} employee(s) still hold this role.`,
        });
      } else {
        Swal.fire({ icon: "error", title: "Delete failed", text: data?.message || err.message });
      }
    }
  };

  const columns = useMemo(() => [
    {
      name: "Role",
      selector: (r) => r.role_name,
      sortable: true,
      cell: (r) => (
        <span>
          <strong>{r.role_name}</strong>
          {r.code && <small className="text-muted ms-2">({r.code})</small>}
          {r.is_system && <Badge color="info" className="ms-2">SYSTEM</Badge>}
        </span>
      ),
      grow: 2,
    },
    {
      name: "Description",
      selector: (r) => r.description,
      cell: (r) => <small className="text-muted">{r.description || "—"}</small>,
      grow: 3,
    },
    {
      name: "Permissions",
      cell: (r) => (
        <span>
          {r.code === "super_admin"
            ? <Badge color="dark">ALL</Badge>
            : <Badge color="secondary">{(r.permissions || []).length}</Badge>}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Created",
      selector: (r) => r.createdAt,
      format: (r) => moment(r.createdAt).format("DD MMM YYYY"),
      width: "130px",
    },
    {
      name: "Actions",
      sortable: false,
      center: true,
      width: "120px",
      omit: !rbac.canAny(["roles.update", "roles.delete"]),
      cell: (r) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>

          <DropdownMenu>
            {rbac.can("roles.update") && (
              <DropdownItem
                onClick={() => navigate(`/role-management/edit/${r._id}`)}
              >
                Edit
                <FaPen className="ms-1" />
              </DropdownItem>
            )}

            {rbac.can("roles.delete") && !r.is_system && (
              <DropdownItem onClick={() => onDelete(r)}>
                Delete
                <FaTrashAlt className="ms-1 text-danger" />
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
    // {
    //   name: "Actions",
    //   width: "140px",
    //   omit: !rbac.canAny(['roles.update', 'roles.delete']),
    //   cell: (r) => (
    //     <div className="d-flex gap-2 align-items-center">
    //       {rbac.can("roles.update") && (
    //         <Button size="sm" color="link" title="Edit" onClick={() => navigate(`/role-management/edit/${r._id}`)}>
    //           <FaPen />
    //         </Button>
    //       )}
    //       {rbac.can("roles.delete") && !r.is_system && (
    //         <Button size="sm" color="link" title="Delete" onClick={() => onDelete(r)}>
    //           <FaTrashAlt color="red" />
    //         </Button>
    //       )}
    //     </div>
    //   ),
    // },
  ], [rbac, navigate]);

  return (
    <Fragment>
      <Row className="mb-3 align-items-center">
        <Col md="6">
          {/* <h4 className="mb-0">
            <FaUsersCog className="me-2" />
            Role Management
          </h4> */}
          <h5 className="fw-bolder">Role Management</h5>
        </Col>
        <Col md="4">
          <Input
            placeholder="Search by name, code, description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md="2" className="text-end">
          {rbac.can("roles.create") && (
            <Button className="btn btn-primary d-flex align-items-center ms-3" onClick={() => navigate("/role-management/create")}>
              <PlusCircle size={16} className="me-1" /> Add New Role
            </Button>
          )}
        </Col>
      </Row>

      <DataTable
        columns={columns}
        data={roles}
        progressPending={loading}
        pagination
        highlightOnHover
        persistTableHead
      />
    </Fragment>
  );
};

export default RolesTable;
